import React from "react";
import HexColorPopover from "../HexColorPopover";
import { PostGraphicPickerProps } from "../../post/types";
import { SquarePlus, Check, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select";
import { Label } from "../Label";

// CSS styles for custom color buttons
const customButtonStyles = `
.color-custom-btn {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ff6b6b, #ffd93d, #6bcb77, #4d96ff, #9b59b6);
}

.color-custom-btn .inner {
  background: #fff;
  border-radius: 4px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
`;

const PostGraphicPicker: React.FC<PostGraphicPickerProps> = ({
  isMobile,
  generalInstructions,
  bgColor,
  textColor,
  fontSize,
  fontFamily,
  setBgColor,
  setTextColor,
  setFontSize,
  setFontFamily,
  isPreviewMode = false,
  postContent = "",
  setPostContent,
}) => {
  const [bgType, setBgType] = React.useState<"solid" | "gradient">(bgColor.type);

  const [gradientFrom, setGradientFrom] = React.useState(bgColor.from || "#2563eb");

  const [gradientTo, setGradientTo] = React.useState(bgColor.to || "#ec4899");

  const [gradientDirection, setGradientDirection] = React.useState(bgColor.direction || "to right");

  const [googleFonts, setGoogleFonts] = React.useState<string[]>([]);
  const [customFontSize, setCustomFontSize] = React.useState<number | string>(fontSize || 24);
  const [pendingFontSize, setPendingFontSize] = React.useState<number | string | null>(null);
  const [isEditingCustomSize, setIsEditingCustomSize] = React.useState(false);
   const instructionsText =
  Array.isArray(generalInstructions)
    ? generalInstructions.join(" ")
    : generalInstructions || "";
const [maxFontSize, setMaxFontSize] = React.useState(getMaxFontSize(instructionsText));
const [defaultSizes, setDefaultSizes] = React.useState(
  [16, 20, 24, 32, 36, 40, 44, 48, 56, 72].filter((size) => size <= getMaxFontSize(instructionsText))
);

  // Load Google Fonts list
  React.useEffect(() => {
    const loadGoogleFonts = async () => {
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_FONTS_API_KEY;
        const response = await fetch(
          `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`,
        );
        const data = await response.json();
        const fontNames = data.items
  .map((font: { family: string }) => font.family)
  .filter(
    (font: string) =>
      font !== "Material Icons" &&
      font !== "Material Icons Outlined" &&
      font !== "Material Symbols Outlined" &&
      font !== "Material Symbols Rounded" && font !== "Archivo Black"
  );
        setGoogleFonts(fontNames);
      } catch (error) {
        console.error("Failed to load Google Fonts:", error);
        // Fallback to popular fonts
        setGoogleFonts([
          "Archivo",
          "Arimo",
          "Barlow",
          "Bebas Neue",
          "DM Sans",
          "Figtree",
          "Fira Sans",
          "Google Sans",
          "Heebo",
          "IBM Plex Sans",
          "Inconsolata",
          "Inter",
          "Kanit",
          "Lato",
          "Lobster Two",
          "Lora",
          "Manrope",
          "Merriweather",
          "Montserrat",
          "Mulish",
          "Noto Sans",
          "Noto Sans JP",
          "Noto Sans KR",
          "Noto Sans TC",
          "Noto Serif",
          "Nunito",
          "Nunito Sans",
          "Open Sans",
          "Oswald",
          "Outfit",
          "Playfair Display",
          "Poppins",
          "Prompt",
          "PT Sans",
          "Quicksand",
          "Raleway",
          "Roboto",
          "Roboto Condensed",
          "Roboto Mono",
          "Roboto Slab",
          "Rubik",
          "Saira",
          "Titillium Web",
          "Ubuntu",
          "Work Sans",
        ]);
      }
    };
    loadGoogleFonts();
  }, []);

  // Dynamically load selected Google Font
  React.useEffect(() => {
    if (fontFamily && !document.getElementById(`font-${fontFamily.replace(/\s/g, "-")}`)) {
      const link = document.createElement("link");
      link.id = `font-${fontFamily.replace(/\s/g, "-")}`;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s/g, "+")}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  }, [fontFamily]);

  // Sync local gradient state with bgColor prop changes
  React.useEffect(() => {
    if (bgColor.type === "gradient") {
      setGradientFrom(bgColor.from || "#2563eb");
      setGradientTo(bgColor.to || "#ec4899");
      setGradientDirection(bgColor.direction || "to right");
    }
    setBgType(bgColor.type);
  }, [bgColor]);

  // Sync changes with parent setBgColor
  React.useEffect(() => {
    if (bgType === "gradient") {
      setBgColor({
        type: "gradient",
        from: gradientFrom,
        to: gradientTo,
        direction: gradientDirection,
      });
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgType, gradientFrom, gradientTo, gradientDirection]);

  /* ---------------- Background Style ---------------- */

  const backgroundStyle =
    bgType === "solid"
      ? bgColor.color || "#000000"
      : `linear-gradient(${gradientDirection}, ${gradientFrom}, ${gradientTo})`;

       // Helper to get max font size for text length
      function getMaxFontSize(text: string) {
        const len = text.length
        if (len <= 15) return 72;
        if (len <= 40) return 56;
        if (len <= 80) return 44;
        if (len < 120) return 36;
        if (len < 160) return 28;
        if (len <= 200) return 20;
        return 16;
      }

      const effectiveFontSize = Math.min(
  typeof fontSize === "number" ? fontSize : parseInt(fontSize || "24", 10),
  getMaxFontSize(instructionsText),
);

// Recalculate when instructionsText changes (or on mount)
React.useEffect(() => {
  const max = getMaxFontSize(instructionsText);
  setMaxFontSize(max);
  setDefaultSizes([16, 20, 24, 32, 36, 40, 44, 48, 56, 72].filter((size) => size <= max));

   // Remove custom size if it exceeds new max
  if (typeof customFontSize === "number" && customFontSize > max) {
    setCustomFontSize("");
    // Optionally, also reset fontSize to a safe default
    if (typeof fontSize === "number" && fontSize > max) {
      setFontSize(max);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [instructionsText]);

  return (
    <>
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: customButtonStyles }} />
      {/* Info */}
      <div>
        <p className="text-gray-500  text-md leading-relaxed text-center my-4">
          Design how your promotional post will appear on social media.
        </p>

        {/* Preview */}
        <div
          className="w-full md:w-[70%] min-h-[160px]
              rounded-xl flex items-center justify-center
              text-center p-4  border-gray-300 mx-auto transition-all overflow-hidden"
          style={{
            background: backgroundStyle,
            color: textColor,
            fontFamily: fontFamily,
            fontSize: `${effectiveFontSize}px`,
            border: "3px dashed #9CA3AF",
          }}
        >
          <p className="font-semibold leading-relaxed break-words max-w-full">
            {instructionsText}
          </p>
        </div>
      </div>

      {/* Post Content Field - only show in preview mode */}
      {isPreviewMode && (
        <div className="mb-6">
          <Label className="text-md font-semibold text-[#8c8c8c] mb-3 block">
            POST CONTENT
          </Label>
          <textarea
            value={postContent}
            onChange={(e) => setPostContent?.(e.target.value)}
            className="w-full min-h-[120px] p-4 border border-gray-200 rounded-lg text-sm 
                       focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                       resize-vertical"
            placeholder="Enter your post content here..."
            rows={6}
          />
          <p className="text-xs text-gray-500 mt-2">
            Edit the content that will be posted to your selected social media
            platforms.
          </p>
        </div>
      )}

      {/* Layout */}
      <div
        className={`
        gap-6
      grid mt-4
      ${isMobile ? "grid-cols-1" : "grid-cols-2"}
     `}
      >
        {/* ================= BACKGROUND ================= */}
        <div className="flex flex-col">
          <p
            className="text-sm text-gray-500 mb-3"
            style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
          >
            BACKGROUND
          </p>

          {/* ---------- SOLID COLORS ---------- */}
          {bgType === "solid" && (
            <div className="flex gap-4 flex-wrap mb-4">
              {(() => {
                const predefinedColors = [
                  "#000000",
                  "#ffffff",
                  "#ffe11a",
                  "#45bf55",
                  "#0288d1",
                ];
                const isCustomColor =
                  bgType === "solid" &&
                  !predefinedColors.includes(bgColor.color || "");

                return (
                  <>
                    {predefinedColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          setBgType("solid");
                          setBgColor({ type: "solid", color });
                        }}
                        className={`w-10 h-10 border-2 transition-all
              ${
                bgType === "solid" && bgColor.color === color
                  ? "border-black scale-105"
                  : "border-gray-200 hover:border-gray-400"
              }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}

                    {/* Custom Color Icon */}
                    <div className="relative">
                      {/* Visible Icon */}
                      <div
                        className={`color-custom-btn transition-all ${
                          isCustomColor ? "scale-105" : ""
                        }`}
                      >
                        <div className="inner">
                          <SquarePlus size={16} color="#5a6a7e" />
                        </div>
                      </div>

                      {/* REAL PICKER */}
                      <HexColorPopover
                        color={bgColor.color || "#000000"}
                        onChange={(color) => {
                          setBgType("solid");
                          setBgColor({ type: "solid", color });
                        }}
                      >
                        <button
                          type="button"
                          aria-label="Custom background colour"
                          className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                        />
                      </HexColorPopover>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ---------- GRADIENT ---------- */}
          {bgType === "gradient" && (
            <div className="flex items-end gap-4 flex-wrap mb-4">
              {/* From */}
              <div className="flex flex-col">
                {/* <p className="text-xs mb-1">From</p> */}
                <div className="relative w-10 h-10">
                  <div
                    className="w-10 h-10 flex items-center justify-center
          border-2 border-black rounded-md transition-all cursor-pointer scale-105"
                    style={{ backgroundColor: gradientFrom }}
                  >
                    <SquarePlus size={16} className="text-white" />
                  </div>
                  <HexColorPopover
                    color={gradientFrom}
                    onChange={setGradientFrom}
                  >
                    <button
                      type="button"
                      aria-label="Gradient start colour"
                      className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                    />
                  </HexColorPopover>
                </div>
              </div>

              {/* To */}
              <div className="flex flex-col">
                {/* <p className="text-xs mb-1">To</p> */}
                <div className="relative w-10 h-10">
                  <div
                    className="w-10 h-10 flex items-center justify-center
          border-2 border-black rounded-md transition-all cursor-pointer scale-105"
                    style={{ backgroundColor: gradientTo }}
                  >
                    <SquarePlus size={16} className="text-white" />
                  </div>
                  <HexColorPopover
                    color={gradientTo}
                    onChange={setGradientTo}
                  >
                    <button
                      type="button"
                      aria-label="Gradient end colour"
                      className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                    />
                  </HexColorPopover>
                </div>
              </div>

              {/* Direction */}
              <div className={`flex flex-col ${isMobile ? "" : "ml-auto"} min-w-[160px]`}>
                {/* <Label className="text-xs mb-1">Direction</Label> */}
                <Select
                  value={gradientDirection}
                  onValueChange={(value) => setGradientDirection(value)}
                >
                  <SelectTrigger className="border rounded-lg px-3 py-2 text-md">
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to right">Left → Right</SelectItem>
                    <SelectItem value="to left">Right → Left</SelectItem>
                    <SelectItem value="to bottom">Top → Bottom</SelectItem>
                    <SelectItem value="to top">Bottom → Top</SelectItem>
                    <SelectItem value="to bottom right">Diagonal ↘</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Toggle */}
          <div className="flex bg-white border border-gray rounded-lg w-fit">
            <button
              onClick={() => {
                setBgType("solid");
                setBgColor({
                  type: "solid",
                  color: bgColor.type === "solid" ? bgColor.color : "#000000",
                });
              }}
              className={`w-32 px-4 py-1.5 rounded-md text-md transition-all font-semibold ${
                bgType === "solid"
                  ? "bg-primary text-white shadow-sm"
                  : "text-[#4b5563] hover:text-gray-800"
              }`}
            >
              Solid
            </button>

            <button
              onClick={() => {
                setBgType("gradient");
                setBgColor({
                  type: "gradient",
                  from: gradientFrom,
                  to: gradientTo,
                  direction: gradientDirection,
                });
              }}
              className={`w-32 px-4 py-1.5 rounded-md text-md transition-all font-semibold ${
                bgType === "gradient"
                  ? "bg-primary text-white shadow-sm"
                  : "text-[#4b5563] hover:text-gray-800"
              }`}
            >
              Gradient
            </button>
          </div>
        </div>

        {/* ================= TEXT COLOR ================= */}
        <div
          className={`flex flex-col w-full ${!isMobile ? "justify-self-end" : ""}`}
        >
          <p
            className="text-sm text-gray-500 mb-3 text-left"
            style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
          >
            TEXT COLOR
          </p>

          <div className="flex gap-4 flex-wrap ">
            {(() => {
              const predefinedTextColors = [
                "#000000",
                "#ffffff",
                "#ffe11a",
                "#45bf55",
                "#0288d1",
              ];
              const isCustomTextColor =
                !predefinedTextColors.includes(textColor);

              return (
                <>
                  {predefinedTextColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setTextColor(color)}
                      className={`w-10 h-10 border-2 transition-all
                      ${
                        textColor === color
                          ? "border-black scale-105"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}

                  {/* Custom Text Color Picker */}
                  <div className="relative">
                    {/* Visible Icon */}
                    <div
                      className={`color-custom-btn transition-all ${
                        isCustomTextColor ? "scale-105" : ""
                      }`}
                    >
                      <div className="inner">
                        <SquarePlus size={16} color="#5a6a7e" />
                      </div>
                    </div>

                    {/* REAL PICKER */}
                    <HexColorPopover
                      color={textColor}
                      onChange={setTextColor}
                    >
                      <button
                        type="button"
                        aria-label="Custom text colour"
                        className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                      />
                    </HexColorPopover>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* ================= FONT FAMILY ================= */}
        <div className="flex flex-col">
          <p
            className="text-sm text-gray-500"
            style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
          >
            FONT FAMILY
          </p>

          <div className="mt-2">
            <Select
              value={fontFamily}
              onValueChange={(value) => setFontFamily(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select font family" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {[...googleFonts]
                  .slice(0, 50)
                  .sort((a, b) => a.localeCompare(b))
                  .map((font) => (
                    <SelectItem key={font} value={font}>
                      <span style={{ fontFamily: font }}>{font}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ================= FONT SIZE ================= */}
        <div
          className={`flex flex-col w-full ${!isMobile ? "justify-self-end" : ""}`}
        >
          <Label
            className="text-sm text-gray-500"
            style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
          >
            FONT SIZE
          </Label>

          <div className="space-y-2 w-full mt-2">
            {/* Show dropdown when not editing custom size */}
            {!isEditingCustomSize && (
              <Select
                value={fontSize.toString()}
                onValueChange={(value) => {
                  if (value === "custom") {
                    setIsEditingCustomSize(true);
                    setPendingFontSize(
                      typeof customFontSize === "number" ? customFontSize : 24,
                    );
                  } else {
                    const newSize = parseInt(value);
                    setIsEditingCustomSize(false);
                    setFontSize(newSize);
                    // Don't update customFontSize here - preserve user's custom value
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select font size" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const hasCustomSize =
                      typeof customFontSize === "number" &&
                      !defaultSizes.includes(customFontSize);

                    return (
                      <>
                        {/* Show custom size if user has created one and it's not in default list */}
                        {hasCustomSize && (
                          <SelectItem
                            key={`custom-${customFontSize}`}
                            value={customFontSize.toString()}
                          >
                            {customFontSize}px (custom)
                          </SelectItem>
                        )}

                        {/* Show default sizes */}
                        {defaultSizes.map((size) => (
                          <SelectItem key={size} value={size.toString()}>
                            {size}px
                          </SelectItem>
                        ))}

                        <SelectItem value="custom">Custom Size</SelectItem>
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
            )}

            {/* Custom Size Input - only show when editing */}
            {isEditingCustomSize && (
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="8"
                    max={maxFontSize}
                    value={
                      pendingFontSize !== null
                        ? pendingFontSize
                        : customFontSize
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string while typing
                      if (value === "") {
                        setPendingFontSize("");
                        return;
                      }
                      const numValue = parseInt(value);
                      // Store in pending state without updating actual fontSize
                      if (!isNaN(numValue)) {
                        setPendingFontSize(numValue);
                      }
                    }}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    placeholder={`24`}
                    autoFocus
                  />
                  <span className="text-sm text-gray-600 font-medium">px</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const value: number | null =
                        typeof pendingFontSize === "string"
                          ? pendingFontSize === ""
                            ? null
                            : parseInt(pendingFontSize)
                          : pendingFontSize;

                      const fallback = maxFontSize; // fallback to max allowed for this content

                      if (
                        value !== null &&
                        !isNaN(value) &&
                        value >= 8 &&
                        value <= maxFontSize
                      ) {
                        setCustomFontSize(value);
                        setFontSize(value);
                      } else {
                        // fallback: use maxFontSize if over, 8 if under, or maxFontSize if invalid
                        const safeValue =
                          value === null || isNaN(value)
                            ? fallback
                            : value > maxFontSize
                              ? fallback
                              : value < 8
                                ? 8
                                : fallback;
                        setCustomFontSize(safeValue);
                        setFontSize(safeValue);
                      }
                      setPendingFontSize(null);
                      setIsEditingCustomSize(false);
                    }}
                    className="p-2 bg-primary text-white hover:bg-primary/90 rounded transition-colors"
                    title="Apply font size"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setPendingFontSize(null);
                      setIsEditingCustomSize(false);
                    }}
                    className="p-2 bg-gray-100 text-black hover:bg-gray-200 rounded transition-colors"
                    title="Cancel changes"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PostGraphicPicker;
